import { useState, useRef, useEffect } from 'react'
import { Trash2, CalendarClock, User, Check, X, ChevronDown, Plus, Search } from 'lucide-react'
import { useApp } from '@/context/AppContext'
import { useAllUsers } from '@/lib/useAllUsers'
import { useClickOutside } from '@/lib/useClickOutside'
import { deptColor } from '@/lib/departments'
import { RecurrenceControl } from '@/components/RecurrenceControl'
import { formatDate, picColor, parsePics } from '@/lib/utils'
import type { Subtask, SubSubtask } from '@/lib/types'

// Satu baris sub-subtask: checkbox + judul (klik untuk edit) + hapus.
function SubSubtaskRow({ ss, locked }: { ss: SubSubtask; locked: boolean }) {
  const { toggleSubSubtask, renameSubSubtask, deleteSubSubtask } = useApp()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(ss.title)
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => { if (editing) ref.current?.select() }, [editing])
  const commit = () => {
    const t = draft.trim()
    if (t && t !== ss.title) renameSubSubtask(ss.id, t)
    setEditing(false)
  }
  return (
    <div className="group/ss flex items-center gap-2 py-0.5">
      <button
        onClick={() => { if (!locked) toggleSubSubtask(ss.id, !ss.completed) }}
        className="flex-shrink-0 w-3.5 h-3.5 rounded border transition-all focus:outline-none"
        style={{ borderColor: ss.completed ? '#16A34A' : '#C8C7BF', backgroundColor: ss.completed ? '#16A34A' : 'white' }}
        aria-label={ss.completed ? 'Tandai belum selesai' : 'Tandai selesai'}
      >
        {ss.completed && (
          <svg viewBox="0 0 16 16" fill="none" className="w-full h-full p-0.5">
            <path d="M3 8l3.5 3.5L13 5" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </button>
      {editing ? (
        <input
          ref={ref}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); commit() }
            if (e.key === 'Escape') { setDraft(ss.title); setEditing(false) }
          }}
          className="flex-1 text-[11px] font-body px-1.5 py-0.5 bg-white border border-[#C8C7BF] rounded text-[#1A1916] focus:outline-none focus:border-[#1A1916]"
        />
      ) : (
        <span
          onClick={() => { if (!locked) setEditing(true) }}
          title={locked ? undefined : 'Klik untuk edit'}
          className={`flex-1 text-[11px] font-body leading-relaxed cursor-text transition-colors ${
            ss.completed ? 'line-through text-[#C0BDB6]' : 'text-[#4A4844] hover:text-[#1A1916]'
          }`}
        >
          {ss.title}
        </span>
      )}
      {!locked && !editing && (
        <button
          onClick={() => deleteSubSubtask(ss.id)}
          className="flex-shrink-0 p-0.5 rounded text-[#C8C7BF] hover:text-[#DC2626] transition-all"
          aria-label="Hapus sub-subtask"
        >
          <Trash2 className="w-2.5 h-2.5" />
        </button>
      )}
    </div>
  )
}

// Avatar PIC subtask (bisa lebih dari satu) + dropdown multi-select.
// Kosong = ikut PIC task/project (fallback).
function SubtaskAssignee({ subtask, locked }: { subtask: Subtask; locked: boolean }) {
  const { tasks, getProject, setSubtaskAssignee } = useApp()
  const { users } = useAllUsers()
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const ref = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  // Ketikan dibuang saat ditutup, supaya membuka lagi tidak menyisakan filter lama.
  const close = () => { setOpen(false); setQ('') }
  useClickOutside(ref, close, open)
  useEffect(() => { if (open) searchRef.current?.focus() }, [open])

  const task = tasks.find((t) => t.id === subtask.taskId)
  const project = task ? getProject(task.projectId) : undefined
  const projectDept = project?.department ?? null
  const deptOf = new Map(users.map((u) => [u.username, u.department]))
  const taskPics = parsePics(task?.assignee)
  const fallbackList = taskPics.length ? taskPics : (project?.pic ? [project.pic] : [])
  const fallbackLabel = fallbackList.join(', ')

  const selected = parsePics(subtask.assignee)
  const inherited = selected.length === 0
  // Yang ditampilkan: PIC subtask kalau ada, kalau tidak ikut fallback (1 orang).
  const display = inherited ? fallbackList : selected

  const names = Array.from(new Set([
    ...selected,
    ...users.map((u) => u.username),
  ])).sort((a, b) => a.localeCompare(b))

  const qLower = q.trim().toLowerCase()
  const shown = qLower
    ? names.filter((n) =>
        n.toLowerCase().includes(qLower)
        || (deptOf.get(n) ?? '').toLowerCase().includes(qLower)
        || selected.some((sel) => sel.toLowerCase() === n.toLowerCase()))
    : names

  // Toggle satu nama dalam daftar (dropdown tetap terbuka untuk pilih banyak).
  const toggle = (name: string) => {
    const set = new Set(selected)
    if (set.has(name)) set.delete(name)
    else set.add(name)
    const next = [...set]
    setSubtaskAssignee(subtask.id, next.length ? next.join(';') : null)
  }
  const clearToInherit = () => { setSubtaskAssignee(subtask.id, null); close() }

  const titleText = inherited
    ? (fallbackLabel ? `Ikut PIC: ${fallbackLabel}` : 'Set PIC subtask')
    : `PIC subtask: ${selected.join(', ')}`

  // Tumpukan avatar (maks 3 + sisa).
  const Avatars = () => (
    <span className="flex items-center" style={{ opacity: inherited ? 0.55 : 1 }}>
      {display.slice(0, 3).map((n, idx) => (
        <span
          key={n}
          className="w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-bold text-white ring-1 ring-white"
          style={{ backgroundColor: picColor(n), marginLeft: idx === 0 ? 0 : -5 }}
        >
          {n.charAt(0).toUpperCase()}
        </span>
      ))}
      {display.length > 3 && (
        <span className="ml-0.5 text-[9px] font-body text-[#8A8A85]">+{display.length - 3}</span>
      )}
    </span>
  )

  if (locked) {
    if (display.length === 0) return null
    return (
      <span className="flex-shrink-0" title={titleText}><Avatars /></span>
    )
  }

  return (
    <div ref={ref} className="relative flex-shrink-0">
      <button
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o) }}
        title={titleText}
        aria-label="Set PIC subtask"
        className="flex items-center"
      >
        {display.length > 0 ? (
          <Avatars />
        ) : (
          <span className="w-4 h-4 rounded-full border border-dashed border-[#C8C7BF] flex items-center justify-center text-[#C8C7BF] transition-opacity">
            <User className="w-2.5 h-2.5" />
          </span>
        )}
      </button>

      {open && (
        <div role="listbox" aria-multiselectable className="absolute right-0 top-[calc(100%+4px)] z-30 min-w-[210px] bg-white border border-[#E0DFD8] rounded-xl shadow-xl py-1.5 animate-fade-in">
          <p className="px-3 pb-1.5 text-[10px] uppercase tracking-wider text-[#C0BDB6] font-body font-semibold">
            PIC subtask · pilih ≥ 1
          </p>
          <div className="px-2 pb-1.5 mb-1 border-b border-[#F2F1EC]">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-[#C0BDB6] pointer-events-none" />
              <input
                ref={searchRef}
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  e.stopPropagation()
                  if (e.key === 'Escape') close()
                  if (e.key === 'Enter' && shown.length === 1) { e.preventDefault(); toggle(shown[0]) }
                }}
                placeholder="Cari nama atau departemen"
                className="w-full pl-7 pr-2.5 py-1.5 bg-white border border-[#E8E7E3] rounded-lg text-[11px] font-body text-[#1A1916] placeholder:text-[#C0BDB6] focus:outline-none focus:border-[#C7D2FE] transition-colors"
              />
            </div>
          </div>
          <div className="max-h-56 overflow-auto">
          {names.length === 0 && (
            <p className="px-3 py-1.5 text-[11px] text-[#AEADA8] font-body">Belum ada user terdaftar</p>
          )}
          {names.length > 0 && shown.length === 0 && (
            <p className="px-3 py-1.5 text-[11px] text-[#AEADA8] font-body">Tidak ada yang cocok dengan "{q.trim()}"</p>
          )}
          {shown.map((name) => {
            const on = selected.some((n) => n.toLowerCase() === name.toLowerCase())
            return (
              <button
                key={name}
                type="button"
                role="option"
                aria-selected={on}
                onClick={(e) => { e.stopPropagation(); toggle(name) }}
                className={`w-full flex items-center gap-2 px-3 py-1.5 text-[11px] font-body transition-colors ${
                  on ? 'bg-[#F4F3F0] text-[#1A1916] font-semibold' : 'text-[#6B6860] hover:bg-[#F4F3F0]'
                }`}
              >
                <span
                  className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border transition-all"
                  style={{ borderColor: on ? '#6366F1' : '#C8C7BF', backgroundColor: on ? '#6366F1' : 'white' }}
                >
                  {on && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                </span>
                <span className="w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-bold text-white flex-shrink-0" style={{ backgroundColor: picColor(name) }}>
                  {name.charAt(0).toUpperCase()}
                </span>
                <span className="flex-1 text-left truncate">{name}</span>
                {(() => {
                  const dep = deptOf.get(name)
                  if (!dep || dep === projectDept) return null
                  const c = deptColor(dep)
                  return (
                    <span className="text-[9px] font-body font-semibold px-1 py-0.5 rounded border flex-shrink-0" style={{ backgroundColor: c.bg, color: c.text, borderColor: c.border }}>
                      {dep}
                    </span>
                  )
                })()}
              </button>
            )
          })}
          </div>
          {selected.length > 0 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); clearToInherit() }}
              className="w-full flex items-center gap-2 px-3 py-1.5 mt-1 pt-1.5 border-t border-[#F2F1EC] text-[11px] font-body text-[#DC2626] hover:bg-[#FEF2F2] transition-colors"
            >
              <X className="w-3 h-3 flex-shrink-0" /> Kosongkan {fallbackLabel ? `(ikut PIC ${fallbackLabel})` : ''}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

export function SubtaskItem({ subtask, locked = false }: { subtask: Subtask; locked?: boolean }) {
  const {
    toggleSubtask, deleteSubtask, renameSubtask, setSubtaskOutput, setSubtaskDeadline, setSubtaskDescription,
    setSubtaskRecurrence,
    getSubSubtasks, createSubSubtask, toggleSubSubtask, renameSubSubtask, deleteSubSubtask,
  } = useApp()
  // Deskripsi subtask (opsional) — edit inline
  const [editingDesc, setEditingDesc] = useState(false)
  const [draftDesc, setDraftDesc] = useState(subtask.description ?? '')
  const descRef = useRef<HTMLTextAreaElement>(null)
  useEffect(() => { if (editingDesc) descRef.current?.focus() }, [editingDesc])
  const commitDesc = () => {
    const v = draftDesc.trim()
    if (v !== (subtask.description ?? '')) setSubtaskDescription(subtask.id, v)
    setEditingDesc(false)
  }
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(subtask.title)
  const inputRef = useRef<HTMLInputElement>(null)

  // ── Sub-subtask (opsional) ──
  const children = getSubSubtasks(subtask.id)
  const hasChildren = children.length > 0
  const childDone = children.filter((c) => c.completed).length
  const [expanded, setExpanded] = useState(false)
  const [addingSS, setAddingSS] = useState(false)
  const [newSSTitle, setNewSSTitle] = useState('')
  const ssInputRef = useRef<HTMLInputElement>(null)
  useEffect(() => { if (addingSS) ssInputRef.current?.focus() }, [addingSS])
  const addSubSub = async () => {
    const t = newSSTitle.trim()
    if (!t) return
    try { await createSubSubtask(subtask.id, t); setNewSSTitle('') } catch { /* toast via context */ }
    ssInputRef.current?.focus()
  }

  // Output / hasil
  const [editingOutput, setEditingOutput] = useState(false)
  const [draftOutput, setDraftOutput] = useState(subtask.output ?? '')
  const outputRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => { if (editing) inputRef.current?.focus() }, [editing])
  useEffect(() => { if (editingOutput) outputRef.current?.focus() }, [editingOutput])

  const startEdit = () => { if (locked) return; setDraft(subtask.title); setEditing(true) }
  const commitEdit = () => {
    const trimmed = draft.trim()
    if (trimmed && trimmed !== subtask.title) renameSubtask(subtask.id, trimmed)
    setEditing(false)
  }
  const cancelEdit = () => { setDraft(subtask.title); setEditing(false) }

  // Centang → buka pencatatan hasil. Saat punya sub-subtask, status otomatis (non-klik).
  const handleToggle = () => {
    if (locked || hasChildren) return
    const next = !subtask.completed
    toggleSubtask(subtask.id, next)
    if (next) { setDraftOutput(subtask.output ?? ''); setEditingOutput(true) }
  }

  const commitOutput = () => {
    const v = draftOutput.trim()
    if (v !== (subtask.output ?? '')) setSubtaskOutput(subtask.id, v)
    setEditingOutput(false)
  }

  // Deadline subtask
  const [editingDeadline, setEditingDeadline] = useState(false)
  const saveDeadline = (value: string) => { setSubtaskDeadline(subtask.id, value || null); setEditingDeadline(false) }
  const fmtD = (d: string) => new Date(d + 'T00:00:00').toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })
  const dueOverdue = !!subtask.deadline && !subtask.completed &&
    new Date(subtask.deadline + 'T00:00:00') < new Date(new Date().setHours(0, 0, 0, 0))

  return (
    <div className="group rounded-lg hover:bg-[#F8F7F3] transition-colors px-1 py-1">
      <div className="flex items-center gap-2.5 py-0.5">
        {/* Checkbox — otomatis (non-klik) bila subtask punya sub-subtask */}
        <button
          onClick={handleToggle}
          className={`flex-shrink-0 w-4 h-4 rounded border transition-all focus:outline-none ${hasChildren ? 'cursor-default' : ''}`}
          style={{
            borderColor: subtask.completed ? '#16A34A' : '#C8C7BF',
            backgroundColor: subtask.completed ? '#16A34A' : 'white',
          }}
          title={hasChildren ? 'Status otomatis dari sub-subtask' : undefined}
          aria-label={subtask.completed ? 'Tandai belum selesai' : 'Tandai selesai'}
        >
          {subtask.completed && (
            <svg viewBox="0 0 16 16" fill="none" className="w-full h-full p-0.5">
              <path d="M3 8l3.5 3.5L13 5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </button>

        {/* Title */}
        {editing ? (
          <input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commitEdit() }
              if (e.key === 'Escape') cancelEdit()
            }}
            className="flex-1 text-xs font-body px-1.5 py-0.5 bg-white border border-[#C8C7BF] rounded-md text-[#1A1916] focus:outline-none focus:border-[#1A1916] transition-colors"
          />
        ) : (
          <span
            onClick={startEdit}
            title="Klik untuk edit"
            className={`flex-1 text-xs font-body leading-relaxed cursor-text transition-colors ${
              subtask.completed ? 'line-through text-[#C0BDB6]' : 'text-[#4A4844] hover:text-[#1A1916]'
            }`}
          >
            {subtask.title}
          </span>
        )}

        {/* Toggle sub-subtask + hitungan */}
        {!editing && (
          <button
            onClick={() => setExpanded((o) => !o)}
            title="Sub-subtask"
            aria-label="Buka sub-subtask"
            className="flex-shrink-0 flex items-center gap-1 text-[#C0BDB6] hover:text-[#6B6860] transition-all"
          >
            {hasChildren && <span className="text-[9px] font-body text-[#AEADA8]">{childDone}/{children.length}</span>}
            <ChevronDown className={`w-3 h-3 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </button>
        )}

        {/* PIC subtask — avatar + dropdown (fallback ikut PIC task/project) */}
        {!editing && <SubtaskAssignee subtask={subtask} locked={locked} />}

        {/* Tanggal selesai */}
        {subtask.completed && subtask.tanggalSelesai && !editing && (
          <span className="flex-shrink-0 hidden sm:inline-flex items-center gap-1 text-[9px] font-body font-medium text-[#16A34A]"
            title={`Selesai ${formatDate(subtask.tanggalSelesai)}`}>
            {formatDate(subtask.tanggalSelesai)}
          </span>
        )}

        {/* Deadline subtask — klik untuk set/ubah */}
        {!editing && (
          editingDeadline ? (
            <input
              type="date"
              autoFocus
              defaultValue={subtask.deadline?.slice(0, 10) ?? ''}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveDeadline((e.target as HTMLInputElement).value)
                if (e.key === 'Escape') setEditingDeadline(false)
              }}
              onBlur={(e) => saveDeadline(e.target.value)}
              className="flex-shrink-0 text-[9px] px-1.5 py-0.5 bg-white border border-[#C8C7BF] rounded-md text-[#1A1916] focus:outline-none focus:border-[#1A1916]"
            />
          ) : subtask.deadline ? (
            <button
              onClick={() => { if (!locked) setEditingDeadline(true) }}
              title={locked ? undefined : 'Klik untuk ubah deadline subtask'}
              className={`flex-shrink-0 inline-flex items-center gap-1 text-[9px] font-body font-medium px-1.5 py-0.5 rounded-md ${
                dueOverdue ? 'bg-[#FEF2F2] text-[#DC2626] border border-[#FECACA]' : 'bg-[#F4F3F0] text-[#6B6860] hover:bg-[#EEEEE9]'
              } ${locked ? 'cursor-default' : ''}`}
            >
              <CalendarClock className="w-2.5 h-2.5" />{fmtD(subtask.deadline)}
            </button>
          ) : !locked ? (
            <button
              onClick={() => setEditingDeadline(true)}
              title="Set deadline subtask"
              className="flex-shrink-0 p-1 rounded text-[#C8C7BF] hover:text-[#6366F1] transition-all"
              aria-label="Set deadline subtask"
            >
              <CalendarClock className="w-3 h-3" />
            </button>
          ) : null
        )}

        {/* Rutin (repetitif) — di luar perhitungan % project */}
        {!editing && (
          <RecurrenceControl
            value={subtask.recurrence}
            onChange={(v) => setSubtaskRecurrence(subtask.id, v)}
            disabled={locked}
            size="xs"
          />
        )}

        {/* Delete */}
        {!editing && !locked && (
          <button
            onClick={() => deleteSubtask(subtask.id)}
            className="flex-shrink-0 p-1 rounded text-[#C8C7BF] hover:text-[#DC2626] transition-all"
            aria-label="Hapus subtask"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        )}
      </div>

      {/* Deskripsi subtask (opsional) — objektif / arahan */}
      {editingDesc ? (
        <div className="ml-[26px] mt-1 mb-0.5">
          <textarea
            ref={descRef}
            value={draftDesc}
            onChange={(e) => setDraftDesc(e.target.value)}
            onBlur={commitDesc}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); commitDesc() }
              if (e.key === 'Escape') { setDraftDesc(subtask.description ?? ''); setEditingDesc(false) }
            }}
            rows={2}
            placeholder="Objektif / arahan subtask… (Enter simpan, Shift+Enter baris baru)"
            className="w-full text-[11px] font-body leading-relaxed px-2.5 py-1.5 bg-white border border-[#C8C7BF] rounded-md text-[#1A1916] placeholder:text-[#C0BDB6] focus:outline-none focus:border-[#1A1916] resize-none"
          />
        </div>
      ) : subtask.description ? (
        <button
          onClick={() => { if (locked) return; setDraftDesc(subtask.description ?? ''); setEditingDesc(true) }}
          title={locked ? undefined : 'Klik untuk ubah deskripsi'}
          className={`ml-[26px] mt-1 mb-0.5 block max-w-full text-left text-[11px] font-body text-[#6B6860] leading-relaxed whitespace-pre-wrap ${locked ? 'cursor-default' : 'hover:text-[#1A1916]'}`}
        >
          {subtask.description}
        </button>
      ) : !locked && !editing ? (
        <button
          onClick={() => { setDraftDesc(''); setEditingDesc(true) }}
          className="ml-[26px] mt-0.5 mb-0.5 text-[10px] font-body text-[#C0BDB6] hover:text-[#6366F1] transition-colors"
        >
          + deskripsi
        </button>
      ) : null}

      {/* Hasil / output — hanya saat subtask selesai */}
      {subtask.completed && (
        <div className="ml-[26px] mt-1 mb-0.5">
          {editingOutput ? (
            <textarea
              ref={outputRef}
              value={draftOutput}
              onChange={(e) => setDraftOutput(e.target.value)}
              onBlur={commitOutput}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); commitOutput() }
                if (e.key === 'Escape') setEditingOutput(false)
              }}
              rows={2}
              placeholder="Apa hasil / output dari subtask ini? (Enter untuk simpan)"
              className="w-full text-[11px] font-body leading-relaxed px-2.5 py-1.5 bg-white border border-[#C8C7BF] rounded-md text-[#1A1916] placeholder:text-[#C0BDB6] focus:outline-none focus:border-[#16A34A] resize-none"
            />
          ) : subtask.output ? (
            <button
              onClick={() => { if (locked) return; setDraftOutput(subtask.output ?? ''); setEditingOutput(true) }}
              title={locked ? undefined : 'Klik untuk ubah hasil'}
              className={`w-full text-left text-[11px] font-body text-[#3A3A36] bg-[#F0FAF4] border border-[#BBF7D0] rounded-md px-2.5 py-1.5 transition-colors ${locked ? 'cursor-default' : 'hover:border-[#16A34A]'}`}
            >
              <span className="block text-[8px] uppercase tracking-widest text-[#16A34A] font-bold mb-0.5">Hasil</span>
              <span className="whitespace-pre-wrap leading-relaxed">{subtask.output}</span>
            </button>
          ) : locked ? null : (
            <button
              onClick={() => { setDraftOutput(''); setEditingOutput(true) }}
              className="text-[10px] font-body font-medium text-[#B45309] bg-[#FEF3C7] border border-dashed border-[#FDE68A] rounded-md px-2 py-1 hover:bg-[#FDE9B8] transition-colors"
            >
              + Catat hasil (wajib)
            </button>
          )}
        </div>
      )}

      {/* Sub-subtask — opsional, lebih detail */}
      {expanded && (
        <div className="ml-[26px] mt-1.5 mb-1 pl-3 border-l border-[#EDEBE5] space-y-0.5">
          {children.map((ss) => (
            <SubSubtaskRow key={ss.id} ss={ss} locked={locked} />
          ))}

          {children.length === 0 && !addingSS && (
            <p className="text-[10px] text-[#C0BDB6] font-body py-0.5">Belum ada sub-subtask.</p>
          )}

          {!locked && (
            addingSS ? (
              <div className="flex items-center gap-2 pt-0.5">
                <input
                  ref={ssInputRef}
                  type="text"
                  value={newSSTitle}
                  onChange={(e) => setNewSSTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); addSubSub() }
                    if (e.key === 'Escape') { setAddingSS(false); setNewSSTitle('') }
                  }}
                  placeholder="Nama sub-subtask..."
                  className="flex-1 text-[11px] px-2 py-1 bg-white border border-[#C8C7BF] rounded text-[#1A1916] placeholder:text-[#C8C7BF] focus:outline-none focus:border-[#1A1916]"
                />
                <button
                  onClick={addSubSub}
                  disabled={!newSSTitle.trim()}
                  className="px-2.5 py-1 bg-[#1A1916] text-white rounded text-[11px] font-medium font-body hover:bg-[#2A2A28] disabled:opacity-40 transition-all"
                >
                  Simpan
                </button>
                <button
                  onClick={() => { setAddingSS(false); setNewSSTitle('') }}
                  className="p-1 text-[#C8C7BF] hover:text-[#6B6860] transition-colors"
                  aria-label="Batal"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setAddingSS(true)}
                className="flex items-center gap-1 text-[10px] font-body text-[#6B6860] hover:text-[#1A1916] transition-colors pt-0.5"
              >
                <Plus className="w-3 h-3" /> Tambah sub-subtask
              </button>
            )
          )}
        </div>
      )}
    </div>
  )
}
